import { buffers, eventChannel } from 'redux-saga';
import { delay, fork, put, take, takeLatest } from 'redux-saga/effects';

import { svcAuth } from '@pressreader/services';

import { identityTokenChanged, type IdentityTokenChangedPayload } from '../actions';
import { identityReconcileRequested } from '../actions/identity.internal';

/**
 * How long a token identity change is given to produce a Redux commit of its own before the cache reconcile is
 * requested. Long enough for the slowest normal login/logout round trip (profile + settings) to land, so on
 * every path that commits, that commit's own reconcile has already run and the late request is deduped away.
 */
export const IDENTITY_MISMATCH_RECHECK_DELAY_MS = 10_000;

/**
 * `svcAuth`'s `identityChanged` as a saga channel, carrying the action payload itself — one type for the whole
 * relay, so there is no second shape to keep in step with the contract. It is published through the service's own
 * bind/unbind registry rather than an rxjs observable, so the channel is built directly instead of through
 * `createEventChannel`.
 *
 * The whole payload is snapshotted in the callback, not in the taker: the clock is updated before the callback
 * fan-out, so reading it here pins the previous key, the current key and the generation that belong to THIS event
 * even if takers run later.
 */
function createIdentityChangedChannel() {
    return eventChannel<IdentityTokenChangedPayload>(
        emit => {
            const onIdentityChanged = () =>
                emit({
                    previousKey: svcAuth.previousIdentityKey(),
                    currentKey: svcAuth.identityKey(),
                    generation: svcAuth.identityGeneration(),
                });

            svcAuth.bind('identityChanged', onIdentityChanged);

            return () => svcAuth.unbind('identityChanged', onIdentityChanged);
        },
        // `expanding`: `eventChannel`'s default buffer drops an event outright when no taker is pending, and a
        // login burst must queue instead. An expanding ring buffer never drops — it grows past its initial size
        // (redux-saga's default, 10) rather than overflowing.
        buffers.expanding<IdentityTokenChangedPayload>(),
    );
}

/**
 * Relays token identity transitions into Redux as `identityTokenChanged`. Forked from this module's root saga,
 * which the always-running auth-core root (`./index.ts`) forks in turn, so the signal exists from boot regardless
 * of which features loaded, and independently of the RTK listener middleware — every downstream identity reaction
 * hangs off this action.
 *
 * A pure RELAY: every fact the action carries is the service's. `svcAuth` publishes `identityChanged` for real
 * transitions only, so a plain token rotation (refresh, claims change, anonymous re-issue) and the boot seed (the
 * identity this tab started with, which is not a change) are already silent at the source — one definition of a
 * transition, in `authorization.ts`, instead of a copy re-derived from `tokenUpdated` here. The from-key comes
 * from the same clock (`previousIdentityKey()`) rather than from a mirror kept here: this saga is forked at module
 * import, BEFORE the boot token arrives, so anything primed at fork time would report `anonymous` as the identity
 * the first transition left, whatever the tab actually booted as.
 */
export function* watchIdentityToken() {
    const channel = createIdentityChangedChannel();

    try {
        while (true) {
            const event: IdentityTokenChangedPayload = yield take(channel);

            yield put(identityTokenChanged(event));
        }
    } finally {
        // Cancelled with the root saga (store disposal): drop the `identityChanged` binding along with it.
        channel.close();
    }
}

/**
 * Covers the transitions no auth action accompanies: once a transition has had its settling window, ask for the
 * RTK Query caches to be reconciled to the identity the token now carries. Unconditional on purpose — this saga
 * decides WHEN to ask, never whether the work is needed:
 *
 * - The executor (`runIdentityCommitReconcile`) dedupes by identity GENERATION, so on the paths that do commit
 *   (login, sign-up, logout, account switch) the commit listener has already reconciled this generation and the
 *   request is a no-op. Asking anyway costs one dispatch and a compare.
 * - Testing the slice against the token here was NOT a safe substitute. A commit-less A -> B -> A burst returns
 *   to the key the slice still holds — the keys match while two generations have passed and B's data is sitting
 *   in the cache under A — and `takeLatest` has cancelled the B window that would have caught it. The generation
 *   the executor compares is the only thing that sees that.
 *
 * An action, not a direct call: this saga is eager and the listener that answers it (with the whole policy/scope
 * machinery behind it) is lazy — naming the executor here would pull it onto the eager boot path.
 *
 * SCOPE: repairs cached DATA only. The auth slice itself stays on the previous user for a commit-less transition
 * (this saga commits nothing), so the commit-keyed feature layer (`registerAuthReconcile`'s callbacks) does not
 * run either — a known gap, out of scope here and separable from the cache repair, which the policy-driven
 * reconcile performs on its own.
 *
 * `takeLatest`: a newer transition restarts the window rather than racing the old one.
 */
export function* recheckIdentityMismatch() {
    yield delay(IDENTITY_MISMATCH_RECHECK_DELAY_MS);

    yield put(identityReconcileRequested());
}

function* saga() {
    yield fork(watchIdentityToken);

    yield takeLatest(identityTokenChanged, recheckIdentityMismatch);
}

export { saga };
